export const CHECKOUT_URL = 'https://eat-well-in-20.impultienda.ar/checkout'

export const BRAND = 'Eat Well in 20™'

export const TAGLINE = 'Great food. Even when you have no time.'

export const MAIN_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/8ZLqNXtGH5nZs3j7AUsHEB/ChatGPT_Image_16_sept_2026,_15_15_13.png'

export const MOCKUP_ALT =
  'The complete EAT WELL IN 20 system — five recipe volumes (Chicken, Beef, Fish & Seafood, Pork, Meatless) shown on laptop, tablet and phone'

/* ---------------------------------------------------------------- Hero */

export const HERO_HEADLINE = 'Great food. Even when you have no time.'

export const HERO_BODY: string[] = [
  '20 minutes between “what do I make?” and something delicious on the table.',
  'No complicated prep. No endless ingredient lists. No spending your evening cooking.',
]

export const HERO_EMPHASIS =
  '75 recipes ready to cook. No scrolling. No guessing. Dinner solved.'

export const HERO_SUB =
  'EAT WELL IN 20™ Complete System — Full access. Real meals that actually fit your life: Chicken, Beef, Fish & Seafood, Pork, Meatless.'

export const CTA_LABEL = 'I want my Eat Well in 20™ now →'

/** Launch window, in seconds, counted down from page load. */
export const LAUNCH_WINDOW_SECONDS = 23 * 3600 + 59 * 60 + 55

export const journeySteps: string[] = [
  'Chicken',
  'Beef',
  'Fish & Seafood',
  'Pork',
  'Meatless',
]

/* --------------------------------------------------------------- Proof */

export type Stat = { value: string; label: string }

export const stats: Stat[] = [
  { value: '+3,000', label: 'Families cooking real meals' },
  { value: '5', label: 'Complete volumes' },
  { value: '75', label: 'Ready-to-cook recipes' },
  { value: '4.9/5', label: 'Average rating' },
]

export type Testimonial = {
  initials: string
  quote: string
  name: string
  role: string
}

export const testimonials: Testimonial[] = [
  {
    initials: 'MR',
    quote:
      'I stopped ordering takeout. Real dinners in 20 minutes and saving $300+ a month.',
    name: 'Maria R.',
    role: 'Marketing Manager',
  },
  {
    initials: 'JR',
    quote:
      'My family eats well now. No more scrolling at 5 PM trying to figure out what to cook.',
    name: 'James R.',
    role: 'Father of Three',
  },
  {
    initials: 'SC',
    quote:
      'Cooking doesn’t feel like a project anymore. Everything is straightforward and genuinely delicious.',
    name: 'Sofia C.',
    role: 'Home Cook',
  },
]

/* ---------------------------------------------------------------- Pain */

export const PAIN_HEADLINE =
  'How many more nights are you going to make the same three meals?'

export const PAIN_BODY: string[] = [
  'You want to eat well. You don’t have time to make it complicated. But that’s the trap, isn’t it?',
  'Most recipes assume you have the afternoon off. Two hours. A specialty ingredient list. Endless prep. You don’t. You have 20 minutes.',
  'So you end up ordering takeout again. Or making the same pasta. Or grabbing whatever is easiest because figuring out a real meal feels like too much work.',
]

export const PAIN_EMPHASIS =
  'The real problem isn’t that you don’t know how to cook. It’s that you need recipes built for the life you actually have — not the life a food blogger imagined.'

export const PAIN_CLOSING =
  'What if you could make something genuinely delicious in less time than it takes to order delivery?'

export const painLines: string[] = [
  'You’re tired and still have to figure out what to cook',
  'You don’t have much time and you don’t want to waste it',
  'Online recipes ask for 12 ingredients you don’t have',
  'Actual cooking takes way longer than the time says',
  'You’re making chicken, pasta, or takeout again',
  'Good food feels like it requires more effort than you have',
  'Another night where dinner is whatever is fastest, not what you actually want',
]

/* -------------------------------------------------------------- System */

export const REVEAL_TITLE = 'Eat Well in 20™ complete system'

export const REVEAL_SUB =
  '75 recipes built for real life. Chicken, Beef, Fish & Seafood, Pork, Meatless. Everything solves in the time you actually have.'

export type Volume = {
  number: string
  title: string
  category: string
  line: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Chicken in 20™',
    category: 'Chicken',
    line: '15 chicken recipes from Mediterranean to Asian. The foundation of every fast weeknight cook.',
  },
  {
    number: 'Volume 2',
    title: 'Beef in 20™',
    category: 'Beef',
    line: '15 beef recipes using quick-cooking cuts. Stir-fries, fajitas, one-pan meals. Satisfying and genuinely fast.',
  },
  {
    number: 'Volume 3',
    title: 'Fish & Seafood in 20™',
    category: 'Fish & Seafood',
    line: '15 fish and seafood recipes. Elegant, quick-cooking, and they taste like restaurant meals at home.',
  },
  {
    number: 'Volume 4',
    title: 'Pork in 20™',
    category: 'Pork',
    line: '15 pork recipes using tenderloin and quick-cooking cuts. Underrated, versatile, and delicious.',
  },
  {
    number: 'Volume 5',
    title: 'Meatless in 20™',
    category: 'Meatless',
    line: '15 vegetarian meals. Chickpeas, beans, eggs, cheese. Completely satisfying without meat.',
  },
]

export const recipeDesign: string[] = [
  'Straightforward step-by-step cooking',
  'Practical, easy-to-find ingredients',
  'Fast and efficient preparation',
  'Delicious meals without complicated techniques',
  'Realistic cooking for busy days',
]

export const systemIncludes: string[] = [
  'Real meals that taste good',
  'Recipes that actually work on a weeknight',
  'Variety so you’re not repeating the same meal',
  'Cooking that doesn’t feel like a project',
  'Food you can feel good about serving your family',
  'Time for your life, not spent cooking',
]

export const INCLUDES_KICKER = 'Great food. Even when you have no time.'

/* ------------------------------------------------------------- Bonuses */

export type Bonus = {
  number: string
  title: string
  line: string
  value: string
  image: string
}

export const BONUS_KICKER = 'Remove every remaining obstacle.'

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'The Decision-Free Meal Planner',
    line: 'Stop scrolling at 5 PM wondering what to make. Done-for-you meal rotations, quick selectors and planning tools that make choosing easier.',
    value: '$47',
    image:
      'https://cdn.magicpatterns.com/uploads/nkdevd1pirj2yyHsthoKbP/Diseno_sin_titulo_(9).png',
  },
  {
    number: '02',
    title: 'The Smart Grocery List System',
    line: 'Never waste money on ingredients you won’t use. Organized shopping lists, pantry guides and grocery tools that simplify everything.',
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/jTsxbtDtHH7aUC1XS1e4eP/Diseno_sin_titulo_(12).png',
  },
  {
    number: '03',
    title: 'The Global Ingredient Swap Guide',
    line: 'Missing an ingredient? Can’t find it locally? Practical substitutions for availability, preferences and common dietary needs.',
    value: '$29',
    image:
      'https://cdn.magicpatterns.com/uploads/mG1TGTgLE5aZBSSkkcx1Mz/Diseno_sin_titulo_(10).png',
  },
  {
    number: '04',
    title: 'The 20-Minute Kitchen Workflow Playbook',
    line: 'The real secret is timing. Overlap tasks, start the longest component first, everything finishes together. No rushing, no burning, no cold food.',
    value: '$24',
    image:
      'https://cdn.magicpatterns.com/uploads/doyoEjuSoNHDj2JsinVCtL/Diseno_sin_titulo_(11).png',
  },
  {
    number: '05',
    title: 'The Fast Flavor Sauce & Finishing Guide',
    line: '30 essential sauces, glazes and finishing touches that turn basic ingredients into genuinely flavorful meals. Troubleshooting included.',
    value: '$19',
    image:
      'https://cdn.magicpatterns.com/uploads/siGL142DDEVxRMGUQ6RMcp/Diseno_sin_titulo_(13).png',
  },
]

export const BONUS_TOTAL = '$156 USD'

/* --------------------------------------------------------------- Offer */

export const OFFER_NAME = 'Complete System — 5 Volumes + 5 Bonuses'

export const offerItems: string[] = [
  '5 Complete Volumes — 75 Recipes',
  '5 Strategic Bonuses',
  'Instant digital access in 5 minutes',
  'Lifetime access, on any device',
]

export const pricing = {
  regularPrice: '$97 USD',
  today: '$27',
  afterLaunch: '$47 USD',
  spotsTaken: 68,
  idealFor: 'Anyone who wants real meals fast',
}

/* ---------------------------------------------------------------- Cost */

export type CostRow = { situation: string; result: string }

export const costRows: CostRow[] = [
  {
    situation: 'Another expensive takeout order',
    result: '$25–$40 per meal × 5 nights × 52 weeks.',
  },
  {
    situation: 'Spending 20 minutes searching for a recipe',
    result: 'More time spent searching than cooking.',
  },
  {
    situation: 'Making the same three meals',
    result: 'Bored with your own food.',
  },
  {
    situation: 'Groceries sitting unused',
    result: 'Money wasted. Guilt about the waste.',
  },
  {
    situation: 'Another evening figuring out what to cook',
    result: 'Tired before you even start.',
  },
  {
    situation: 'Believing good food takes more time than you have',
    result: 'Settling for whatever is fastest.',
  },
  {
    situation: 'The same cycle next week',
    result: 'Nothing changes, nothing improves.',
  },
]

export const CLOSING_LINE = 'Tonight can be different.'

export const CLOSING_SUB = 'The next meal can be delicious.'

/* ----------------------------------------------------------------- FAQ */

export type Faq = { question: string; answer: string }

export const faqs: Faq[] = [
  {
    question: 'Does it work if I’m a beginner at cooking?',
    answer:
      'Yes. The recipes use straightforward instructions, familiar cooking methods and practical ingredients. You don’t need advanced cooking skills or complicated techniques to get started.',
  },
  {
    question: 'How do I receive the material?',
    answer:
      'Everything is instant. A download link to all 5 volumes + 5 bonuses arrives in 5 minutes. Access on phone, tablet or computer. Lifetime access — yours forever.',
  },
  {
    question: 'What makes it different from online recipes?',
    answer:
      'Most online recipes are written for food bloggers with unlimited time. These are written for real people with real schedules. Every recipe uses ingredients you can find in a regular supermarket. Every technique is straightforward. No hidden prep. No tricks.',
  },
  {
    question: 'Is the guarantee real?',
    answer:
      'Completely real. No fine print. No questions asked. We stand behind the product because we use it ourselves.',
  },
]

/* -------------------------------------------------------------- Footer */

export const FOOTER_LINE = 'Real meals in realistic time. No compromise on flavor.'

export const footerLinks = {
  email: 'redondoland1@gmail.com',
  social: '@redondoland',
  website: 'www.redondoland.com',
}

export type PurchaseNotice = { name: string; location: string; minutesAgo: number }

export const purchaseNotice: PurchaseNotice = {
  name: 'Maria R.',
  location: 'New York',
  minutesAgo: 4,
}
